import {
    ArrowPathIcon,
    DocumentArrowUpIcon,
    PencilIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import {
    ActionIcon,
    Alert,
    Badge,
    Box,
    Button,
    Card,
    Group,
    Loader,
    Modal,
    Stack,
    Text,
    TextInput,
    Textarea,
    Title
} from '@mantine/core';
import { formatDistanceToNow } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { AiHttpService, type Prompt } from '../../services/aiHttpService';

interface PromptManagementProps {
  className?: string;
}

const PromptManagement: React.FC<PromptManagementProps> = ({ className }) => {
  const aiService = new AiHttpService();
  
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  
  const [promptName, setPromptName] = useState('');
  const [promptContent, setPromptContent] = useState('');

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const prompts = await aiService.getAllPrompts();
      setPrompts(prompts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPrompt = async (prompt: Prompt) => {
    setLoading(true);
    setError(null);

    try {
      const promptData = await aiService.getPrompt(prompt.promptName);
      if (promptData) {
        setSelectedPrompt(promptData);
        setPromptName(promptData.promptName);
        setPromptContent(promptData.content);
        setEditDialogOpen(true);
      } else {
        throw new Error('Prompt not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get prompt details');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!promptName.trim() || !promptContent.trim()) {
      setError('Prompt name and content are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request = {
        promptName: promptName.trim(),
        content: promptContent.trim(),
      };

      await aiService.updatePrompt(selectedPrompt?.promptName || '', request);
      setSuccess('Prompt updated successfully');
      
      setEditDialogOpen(false);
      await loadPrompts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedPrompt(null);
    setPromptName('');
    setPromptContent('');
    setError(null);
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <Box className={className}>
      <Card withBorder>
        <Card.Section withBorder inheritPadding py="xs">
          <Group justify="space-between">
            <Title order={3}>Prompt Management</Title>
            <Button
              variant="outline"
              leftSection={<ArrowPathIcon width={16} height={16} />}
              onClick={loadPrompts}
              disabled={loading}
            >
              Refresh
            </Button>
          </Group>
        </Card.Section>
        
        <Stack p="md" gap="md">
          {error && (
            <Alert color="red" title="Error" onClose={clearMessages} withCloseButton>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert color="green" title="Success" onClose={clearMessages} withCloseButton>
              {success}
            </Alert>
          )}

          {loading && (
            <Group justify="center" py="xl">
              <Loader />
            </Group>
          )}

          {prompts.length === 0 && !loading ? (
            <Text c="dimmed" ta="center" py="xl">
              No prompts found. Prompts should be deployed manually.
            </Text>
          ) : (
            <Stack gap="xs">
              {prompts.map((prompt) => (
                <Card key={prompt.promptName} withBorder>
                  <Group justify="space-between" mb="xs">
                    <Group>
                      <Text fw={500}>{prompt.promptName}</Text>
                      <Badge variant="outline" size="sm">
                        {prompt.content.length} chars
                      </Badge>
                    </Group>
                    <ActionIcon
                      variant="subtle"
                      onClick={() => handleEditPrompt(prompt)}
                      disabled={loading}
                    >
                      <PencilIcon width={16} height={16} />
                    </ActionIcon>
                  </Group>
                  
                  <Text size="sm" c="dimmed" mb="xs">
                    Last updated {formatDistanceToNow(new Date(prompt.lastUpdated), { addSuffix: true })} by {prompt.lastUpdatedBy}
                  </Text>
                  
                  <Text size="sm" lineClamp={2}>
                    {prompt.content}
                  </Text>
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      {/* Edit Prompt Modal */}
      <Modal
        opened={editDialogOpen}
        onClose={handleCloseDialog}
        title={`Edit Prompt: ${selectedPrompt?.promptName}`}
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Prompt Name"
            value={promptName}
            readOnly
            description="Prompt name cannot be changed"
          />
          
          <Textarea
            label="Prompt Content"
            value={promptContent}
            onChange={(e) => setPromptContent(e.currentTarget.value)}
            disabled={loading}
            description={`${promptContent.length} characters`}
            minRows={12}
            autosize
          />
          
          <Group justify="flex-end">
            <Button 
              variant="outline" 
              onClick={handleCloseDialog} 
              disabled={loading}
              leftSection={<XMarkIcon width={16} height={16} />}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSavePrompt} 
              disabled={loading || !promptName.trim() || !promptContent.trim()}
              leftSection={<DocumentArrowUpIcon width={16} height={16} />}
            >
              Update
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default PromptManagement;